import { WebSocketMessageType, type INotificationMessage } from "@src/models";
import { getAccessTokenSync } from "@utils/tokenStore";

const BASE_URL = import.meta.env.VITE_EXTRACTION_NOTIFICATION_URL;

interface WebSocketCallbacks {
  onMessage?: (message: INotificationMessage) => void;
  onError?: (error: Event) => void;
  onClose?: (event: CloseEvent) => void;
  onOpen?: () => void;
  onAuthenticated?: () => void;
  onAuthenticationFailed?: (reason: string) => void;
}

class NotificationApi {
  private websockets: Map<string, WebSocket> = new Map();
  private isAuthenticated: Map<string, boolean> = new Map();
  private heartbeatIntervals: Map<string, number> = new Map();

  /**
   * The socket needs a token synchronously here and on every reconnect, so
   * it reads the in-memory store rather than awaiting BeeHive. The auth
   * bridge fills the store before any component that opens a socket mounts.
   */
  private getAccessToken(): string | null {
    return getAccessTokenSync();
  }

  public connectToNotifications(
    doctypeCode: string,
    processId: string,
    callbacks: WebSocketCallbacks = {},
  ): void {
    if (!BASE_URL) {
      const reason = "VITE_EXTRACTION_NOTIFICATION_URL is not configured.";
      console.error(reason);
      callbacks.onAuthenticationFailed?.(reason);
      return;
    }

    if (this.websockets.has(processId)) {
      this.disconnect(processId);
    }

    const accessToken = this.getAccessToken();
    if (!accessToken) {
      const reason = "Cannot connect: Access token not found.";
      console.error(reason);
      callbacks.onAuthenticationFailed?.(reason);
      return;
    }

    const wsUrl = `${BASE_URL}?process_id=${encodeURIComponent(processId)}`;
    const websocket = new WebSocket(wsUrl);
    this.websockets.set(processId, websocket);
    this.isAuthenticated.set(processId, false);

    websocket.onopen = () => {
      callbacks.onOpen?.();
      setTimeout(() => {
        this.authenticate(processId, doctypeCode, accessToken);
      }, 500);
    };

    websocket.onmessage = (event) => {
      this.handleMessage(processId, event, callbacks);
    };

    websocket.onerror = (error) => {
      console.error(`WebSocket error for ${processId}:`, error);
      callbacks.onError?.(error);
    };

    websocket.onclose = (event) => {
      this.disconnect(processId, callbacks.onClose, event);
    };
  }

  private handleMessage(
    processId: string,
    event: MessageEvent,
    callbacks: WebSocketCallbacks,
  ) {
    try {
      const message: INotificationMessage = JSON.parse(event.data);
      switch (message.type) {
        case WebSocketMessageType.AuthenticationSuccess:
          this.isAuthenticated.set(processId, true);
          this.startHeartbeat(processId);
          callbacks.onAuthenticated?.();
          break;

        case WebSocketMessageType.AuthenticationFailed:
        case WebSocketMessageType.AuthenticationTimeout:
          this.isAuthenticated.set(processId, false);
          callbacks.onAuthenticationFailed?.(
            message.message || "Authentication failed",
          );
          this.disconnect(processId);
          break;

        case WebSocketMessageType.Pong:
          break;
      }

      callbacks.onMessage?.(message);
    } catch (error) {
      console.error("Error parsing WebSocket message:", error);
    }
  }

  private authenticate(
    processId: string,
    doctypeCode: string,
    accessToken: string,
  ) {
    const websocket = this.websockets.get(processId);
    if (!websocket || websocket.readyState !== WebSocket.OPEN) {
      console.error(`WebSocket for ${processId} not ready for authentication.`);
      return;
    }

    const authMessage = {
      type: WebSocketMessageType.Authentication,
      token: accessToken,
      doctype_code: doctypeCode,
      timestamp: new Date().toISOString(),
    };

    websocket.send(JSON.stringify(authMessage));
  }

  private startHeartbeat(processId: string) {
    this.stopHeartbeat(processId);
    const interval = window.setInterval(() => {
      this.sendPing(processId);
    }, 25000);
    this.heartbeatIntervals.set(processId, interval);
  }

  private stopHeartbeat(processId: string) {
    const interval = this.heartbeatIntervals.get(processId);
    if (interval) {
      clearInterval(interval);
      this.heartbeatIntervals.delete(processId);
    }
  }

  public sendPing(processId: string): void {
    const websocket = this.websockets.get(processId);
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      websocket.send(
        JSON.stringify({
          type: WebSocketMessageType.Ping,
          timestamp: new Date().toISOString(),
        }),
      );
    }
  }

  public isWebSocketAuthenticated(processId: string): boolean {
    return this.isAuthenticated.get(processId) || false;
  }

  public disconnect(
    processId: string,
    onClose?: (event: CloseEvent) => void,
    event?: CloseEvent,
  ): void {
    this.stopHeartbeat(processId);
    const websocket = this.websockets.get(processId);
    if (websocket) {
      websocket.onopen = null;
      websocket.onmessage = null;
      websocket.onerror = null;
      websocket.onclose = null;
      if (websocket.readyState < WebSocket.CLOSING) {
        websocket.close();
      }
    }

    this.websockets.delete(processId);
    this.isAuthenticated.delete(processId);

    if (event && onClose) {
      onClose(event);
    }
  }

  public disconnectAll(): void {
    const allProcessIds = Array.from(this.websockets.keys());
    allProcessIds.forEach((processId) => this.disconnect(processId));
  }
}

export const notificationApi = new NotificationApi();
